import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  getTournamentV2,
  updateTournamentV2,
  deleteTournamentV2,
  startTournament,
  resetTournament,
  getReadyMatches,
  startTournamentMatchWithGame,
  getTournamentByeInfo,
  resetMatchToReady,
} from '../services/tournamentsV2';
import { getClubTableDevices, assignGameToTable } from '../services/tableDevices';
import { useAuth } from '../contexts/AuthContext';
import {
  TournamentV2,
  ReadyMatch,
} from '../types';
import TournamentParticipantManagement from '../components/TournamentParticipantManagement';
import TournamentEditModal, { TournamentUpdateData } from '../components/TournamentEditModal';
import TournamentMatchManagementV2 from '../components/TournamentMatchManagementV2';
import TournamentBracketTree from '../components/TournamentBracketTree';
import './TournamentDetail.css';

const TournamentDetail: React.FC = () => {
  const { t } = useTranslation();
  const { player } = useAuth();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<TournamentV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'participants' | 'matches' | 'bracket'>('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [matchRefreshKey, setMatchRefreshKey] = useState(0);
  const [byeInfo, setByeInfo] = useState<any>(null);
  
  // Match Start Confirmation Modal State
  const [showStartMatchModal, setShowStartMatchModal] = useState(false);
  const [pendingMatch, setPendingMatch] = useState<ReadyMatch | null>(null);
  const [availableTables, setAvailableTables] = useState<any[]>([]);
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [startingMatch, setStartingMatch] = useState(false);

  const tournamentIdNum = tournamentId ? parseInt(tournamentId, 10) : null;

  useEffect(() => {
    if (tournamentIdNum) {
      loadTournament();
      loadByeInfo();
    }
  }, [tournamentIdNum]);

  const loadTournament = async () => {
    if (!tournamentIdNum) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getTournamentV2(tournamentIdNum);
      setTournament(data);
    } catch (err) {
      console.error('Error loading tournament:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tournament');
    } finally {
      setLoading(false);
    }
  };

  const loadByeInfo = async () => {
    if (!tournamentIdNum) return;
    
    try {
      const data = await getTournamentByeInfo(tournamentIdNum);
      setByeInfo(data);
    } catch (err) {
      console.error('Error loading bye info:', err);
      // Don't set error state for bye info - it's not critical
    }
  };

  const handleUpdate = async (updateData: TournamentUpdateData) => {
    if (!tournament) return;
    
    try {
      await updateTournamentV2(tournament.id, updateData);
      setTournament(prev => prev ? { ...prev, ...updateData } : null);
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating tournament:', err);
      alert(err instanceof Error ? err.message : 'Failed to update tournament');
    }
  };

  const handleDelete = async () => {
    if (!tournament) return;

    try {
      setDeleting(true);
      await deleteTournamentV2(tournament.id);
      // Navigate back to tournament management
      navigate(`/clubs/${tournament.club_id}/tournaments`);
    } catch (err) {
      console.error('Error deleting tournament:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete tournament');
    } finally {
      setDeleting(false);
    }
  };

  const handleStartTournament = async () => {
    if (!tournament) return;
    
    try {
      // Start tournament with walkover logic (no byes needed)
      const result = await startTournament(tournament.id);
      alert(result.message);
      loadTournament(); // Refresh the tournament
      setMatchRefreshKey(prev => prev + 1); // Trigger match refresh
    } catch (err) {
      console.error('Error starting tournament:', err);
      alert(err instanceof Error ? err.message : 'Failed to start tournament');
    }
  };

  const handleResetTournament = async () => {
    if (!tournament) return;
    
    if (!window.confirm(t('tournaments.reset.confirm', { tournamentName: tournament.name }))) {
      return;
    }

    try {
      const result = await resetTournament(tournament.id);
      alert(result.message);
      loadTournament(); // Refresh the tournament
    } catch (err) {
      console.error('Error resetting tournament:', err);
      alert(err instanceof Error ? err.message : 'Failed to reset tournament');
    }
  };

  const handleStartMatch = async (matchId: string, matchData?: ReadyMatch) => {
    // Load available tables
    if (tournament?.club_id) {
      try {
        const tables = await getClubTableDevices(tournament.club_id);
        const available = tables.filter(t => t.status === 'approved' && !t.current_game_id);
        setAvailableTables(available);
      } catch (err) {
        console.error('Error loading tables:', err);
        setAvailableTables([]);
      }
    }
    
    // Set pending match and show modal
    setPendingMatch(matchData || null);
    setShowStartMatchModal(true);
  };

  const confirmStartMatch = async () => {
    if (!pendingMatch) return;
    
    try {
      setStartingMatch(true);
      const { gameId } = await startTournamentMatchWithGame(pendingMatch.match_id);
      
      // If a table is selected, assign it
      if (selectedTableId && player) {
        await assignGameToTable(selectedTableId, gameId, player.id);
        alert(t('tournaments.matches.matchStartedWithTable') || `Match started and assigned to table!`);
      } else {
        alert(t('tournaments.matches.matchStarted') || `Match started! The players can now play this game.`);
      }
      
      // Close modal and refresh
      setShowStartMatchModal(false);
      setPendingMatch(null);
      setSelectedTableId(null);
      loadTournament();
    } catch (err) {
      console.error('Error starting match:', err);
      alert(err instanceof Error ? err.message : 'Failed to start match');
    } finally {
      setStartingMatch(false);
    }
  };

  if (!tournamentIdNum) {
    return <div className="error-message">Invalid tournament ID</div>;
  }

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={loadTournament} className="btn btn-primary">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (!tournament) {
    return <div className="error-message">Tournament not found</div>;
  }

  return (
    <div className="tournament-detail-page">
      {/* Compact Header */}
      <div className="tournament-header-compact">
        <button 
          onClick={() => navigate(`/clubs/${tournament.club_id}/tournaments`)}
          className="btn btn-secondary back-button"
        >
          ← {t('common.back')}
        </button>
        
        <div className="tournament-info-compact">
          <div className="tournament-title-row">
            <h1>{tournament.name}</h1>
            <span className={`status-badge status-${tournament.status}`}>
              {t(`tournaments.status.${tournament.status}`)}
            </span>
          </div>
          {tournament.description && (
            <p className="tournament-description-compact">{tournament.description}</p>
          )}
        </div>

        <div className="tournament-actions-compact">
          {/* Icon Actions (top right) */}
          <div className="icon-actions">
            <button
              onClick={() => setShowEditModal(true)}
              className="btn-icon-light"
              title={t('tournaments.actions.edit')}
            >
              ✏️
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="btn-icon-light btn-icon-danger"
              title={t('tournaments.actions.delete')}
            >
              🗑️
            </button>
          </div>
          
          {/* Primary Actions (below icons) */}
          <div className="primary-actions">
            {tournament.status === 'upcoming' && (
              <button
                onClick={handleStartTournament}
                className="btn btn-success"
              >
                🚀 {t('tournaments.actions.start')}
              </button>
            )}
            
            {tournament.status === 'active' && (
              <button 
                className="btn btn-warning"
                onClick={handleResetTournament}
                title={t('tournaments.reset.tooltip')}
              >
                🔄 {t('tournaments.actions.reset')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 {t('tournaments.tabs.overview')}
        </button>
        <button
          className={`tab-button ${activeTab === 'participants' ? 'active' : ''}`}
          onClick={() => setActiveTab('participants')}
        >
          👥 {t('tournaments.tabs.participants')}
        </button>
        <button
          className={`tab-button ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
        >
          🎯 {t('tournaments.tabs.matches')}
        </button>
        <button
          className={`tab-button ${activeTab === 'bracket' ? 'active' : ''}`}
          onClick={() => setActiveTab('bracket')}
        >
          🌳 {t('tournaments.tabs.bracket')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <TournamentOverview tournament={tournament} byeInfo={byeInfo} />
        )}
        
        {activeTab === 'participants' && (
          <TournamentParticipantManagement
            tournamentId={tournament.id}
            tournamentName={tournament.name}
            isOpen={true}
            onClose={() => {}} // Always open in detail view
            inline={true} // Render inline without modal wrapper
          />
        )}
        
        {activeTab === 'matches' && (
          <TournamentMatchesView 
            tournament={tournament}
            onStartMatch={handleStartMatch}
          />
        )}
        
        {activeTab === 'bracket' && (
          <TournamentBracketView tournament={tournament} />
        )}
      </div>

      {/* Edit Tournament Modal */}
      {showEditModal && (
        <TournamentEditModal
          tournament={tournament}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={async (tournamentId: number, updateData: TournamentUpdateData) => {
            await handleUpdate(updateData);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('tournaments.actions.delete')}</h3>
              <button onClick={() => setShowDeleteModal(false)} className="close-button">&times;</button>
            </div>
            <div className="modal-body">
              <p>{t('tournaments.delete.confirmMessage', { name: tournament.name })}</p>
              <p className="warning-text">{t('tournaments.delete.warning')}</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                onClick={() => setShowDeleteModal(false)} 
                className="btn btn-secondary" 
                disabled={deleting}
              >
                {t('common.cancel')}
              </button>
              <button 
                type="button" 
                onClick={handleDelete} 
                className="btn btn-danger" 
                disabled={deleting}
              >
                {deleting ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Match Management Modal */}
      {showMatchesModal && (
        <TournamentMatchManagementV2
          tournamentId={tournament.id}
          tournamentName={tournament.name}
          clubId={tournament.club_id}
          isOpen={showMatchesModal}
          onClose={() => setShowMatchesModal(false)}
          matchRefreshKey={matchRefreshKey}
        />
      )}

      {/* Start Match Confirmation Modal */}
      {showStartMatchModal && pendingMatch && (
        <div className="modal-overlay match-start-modal-overlay">
          <div className="modal-content match-start-modal">
            <div className="modal-header">
              <h3>🎯 {t('tournaments.matches.startMatchConfirmation')}</h3>
              <button onClick={() => setShowStartMatchModal(false)} className="close-button">&times;</button>
            </div>
            <div className="modal-body">
              {/* Match Summary Card */}
              <div className="match-summary-card">
                <div className="match-round-info">
                  <span className="round-badge">
                    {t('tournaments.matches.round')} {pendingMatch.round_number}
                  </span>
                  <span className="match-badge">
                    {t('tournaments.matches.match')} {pendingMatch.match_number}
                  </span>
                </div>
                <div className="match-players-vs">
                  <span className="player-name">{pendingMatch.player_1_name}</span>
                  <span className="vs-separator">vs</span>
                  <span className="player-name">{pendingMatch.player_2_name}</span>
                </div>
                <div className="match-frames-info">
                  {t('tournaments.framesToPlay')}: <strong>{pendingMatch.frames_to_play}</strong>
                </div>
              </div>

              {/* Table Selection */}
              {availableTables.length > 0 && (
                <div className="table-selection-section">
                  <label className="table-selection-label">
                    📱 {t('tableDevices.selectTable')} <span className="optional-text">({t('common.optional')})</span>
                  </label>
                  <select
                    value={selectedTableId || ''}
                    onChange={(e) => setSelectedTableId(e.target.value ? parseInt(e.target.value) : null)}
                    className="table-select-input"
                  >
                    <option value="">{t('tableDevices.noTableAssignment')}</option>
                    {availableTables.map(table => (
                      <option key={table.id} value={table.id}>
                        {table.table_name} {table.location ? `(${table.location})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="table-hint-text">
                    {t('tableDevices.assignTableHint')}
                  </p>
                </div>
              )}

              {availableTables.length === 0 && (
                <div className="no-tables-notice">
                  <span className="notice-icon">ℹ️</span>
                  <span>{t('tableDevices.noTablesAvailable')}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => {
                  setShowStartMatchModal(false);
                  setPendingMatch(null);
                  setSelectedTableId(null);
                }}
                className="btn btn-secondary"
                disabled={startingMatch}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmStartMatch}
                className="btn btn-success"
                disabled={startingMatch}
              >
                {startingMatch ? t('common.processing') : `🚀 ${t('tournaments.matches.startMatch')}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tournament Overview Component
interface TournamentOverviewProps {
  tournament: TournamentV2;
  byeInfo?: any;
}

const TournamentOverview: React.FC<TournamentOverviewProps> = ({ tournament, byeInfo }) => {
  const { t } = useTranslation();

  return (
    <div className="tournament-overview">
      <div className="overview-grid">
        <div className="overview-card">
          <h3>{t('tournaments.formatLabel')}</h3>
          <p>{t(`tournaments.formats.${tournament.format}`)}</p>
        </div>
        
        <div className="overview-card">
          <h3>{t('tournaments.participantsLabel')}</h3>
          <p>
            {byeInfo?.participant_count || 0} / {tournament.settings?.participant_count || 16} {t('tournaments.maxParticipants')}
            {byeInfo?.bye_count > 0 && (
              <span className="bye-info">
                <br />
                <small>{t('tournaments.byes.count')}: {byeInfo.bye_count}</small>
              </span>
            )}
          </p>
        </div>
        
        <div className="overview-card">
          <h3>{t('tournaments.startDate')}</h3>
          <p>
            {tournament.start_date 
              ? new Date(tournament.start_date).toLocaleDateString()
              : t('common.tbd')
            }
          </p>
        </div>
        
        <div className="overview-card">
          <h3>{t('tournaments.statusLabel')}</h3>
          <span className={`status-badge status-${tournament.status}`}>
            {t(`tournaments.status.${tournament.status}`)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Tournament Matches View Component
interface TournamentMatchesViewProps {
  tournament: TournamentV2;
  onStartMatch: (matchId: string, matchData?: ReadyMatch) => void;
}

const TournamentMatchesView: React.FC<TournamentMatchesViewProps> = ({ tournament, onStartMatch }) => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState<ReadyMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resettingMatch, setResettingMatch] = useState<string | null>(null);

  useEffect(() => {
    loadMatches();
  }, [tournament.id]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getReadyMatches(tournament.id);
      setMatches(data);
    } catch (err) {
      console.error('Error loading matches:', err);
      setError(err instanceof Error ? err.message : 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const handleResetMatch = async (matchId: string) => {
    if (!window.confirm(t('tournaments.matches.confirmReset') || 'Reset this match back to ready state? This will allow restarting the match.')) {
      return;
    }

    try {
      setResettingMatch(matchId);
      await resetMatchToReady(matchId);
      
      alert(t('tournaments.matches.resetSuccess') || 'Match reset successfully! You can now start it again.');
      loadMatches(); // Refresh list
    } catch (err) {
      console.error('Error resetting match:', err);
      alert(err instanceof Error ? err.message : 'Failed to reset match');
    } finally {
      setResettingMatch(null);
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (matches.length === 0) {
    return (
      <div className="empty-state">
        <p>{t('tournaments.matches.noReadyMatches')}</p>
        <p className="help-text">{t('tournaments.matches.noReadyMatchesHelp')}</p>
      </div>
    );
  }

  return (
    <div className="matches-list">
      {matches.map(match => {
        const isPending = match.state === 'pending';
        const isReady = match.state === 'ready';
        const isWalkover = match.state === 'void';
        const isInProgress = match.state === 'in_progress';
        const isCompleted = match.state === 'completed';
        const winnerName = match.player_1_name !== 'TBD' ? match.player_1_name : match.player_2_name;
        
        // Determine card styling
        let cardClass = 'match-card';
        if (isWalkover) cardClass += ' walkover';
        if (isPending) cardClass += ' pending';
        if (isCompleted) cardClass += ' completed';
        if (isInProgress) cardClass += ' in-progress';
        
        return (
          <div key={match.match_id} className={cardClass}>
            <div className="match-info">
              <div className="match-header">
                <span className="match-round">
                  {t('tournaments.matches.round')} {match.round_number}
                </span>
                <span className="match-number">
                  {t('tournaments.matches.match')} {match.match_number}
                </span>
                {/* State Badge */}
                {isPending && (
                  <span className="state-badge pending">
                    {t('tournaments.matches.state.pending')}
                  </span>
                )}
                {isReady && (
                  <span className="state-badge ready">
                    {t('tournaments.matches.state.ready')}
                  </span>
                )}
                {isWalkover && (
                  <span className="state-badge walkover">
                    {t('tournaments.matches.walkover')}
                  </span>
                )}
                {isInProgress && (
                  <span className="state-badge in-progress">
                    {t('tournaments.matches.state.in_progress')}
                  </span>
                )}
                {isCompleted && (
                  <span className="state-badge completed">
                    {t('tournaments.matches.state.completed')}
                  </span>
                )}
              </div>
              <div className="match-players">
                {isWalkover ? (
                  <div className="walkover-result-compact">
                    <span className="winner-name-compact">{winnerName}</span>
                    <span className="winner-label-compact">{t('tournaments.matches.advancesToNextRound')}</span>
                  </div>
                ) : (
                  <>
                    <div className="player">
                      <span className="player-name">
                        {match.player_1_name}
                      </span>
                    </div>
                    <div className="vs">:</div>
                    <div className="player">
                      <span className="player-name">
                        {match.player_2_name}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="match-actions">
              {isPending ? (
                <div className="match-status pending-status">
                  ⏳ {t('tournaments.matches.waitingForPlayers')}
                </div>
              ) : isWalkover ? (
                <div className="match-status walkover-status">
                  ✓ {t('tournaments.matches.completed')}
                </div>
              ) : isCompleted ? (
                <div className="match-result-display">
                  <div className="match-status completed-status">
                    ✓ {t('tournaments.matches.completed')}
                  </div>
                  {!isWalkover && match.slot_1_score !== null && match.slot_1_score !== undefined && match.slot_2_score !== null && match.slot_2_score !== undefined && (
                    <div className="match-score-display">
                      {match.slot_1_score}:{match.slot_2_score}
                    </div>
                  )}
                </div>
              ) : isInProgress ? (
                <>
                  <div className="match-status in-progress-status">
                    ▶ {t('tournaments.matches.state.in_progress')}
                  </div>
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => handleResetMatch(match.match_id)}
                    disabled={resettingMatch === match.match_id}
                    title={t('tournaments.matches.resetTooltip')}
                  >
                    {resettingMatch === match.match_id 
                      ? t('common.processing')
                      : '🔄 ' + t('tournaments.matches.reset')
                    }
                  </button>
                </>
              ) : isReady ? (
                <button
                  className="btn btn-primary"
                  onClick={() => onStartMatch(match.match_id, match)}
                  title={t('tournaments.matches.startGameTooltip')}
                >
                  {t('tournaments.matches.startGame')}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Tournament Bracket View Component
interface TournamentBracketViewProps {
  tournament: TournamentV2;
}

const TournamentBracketView: React.FC<TournamentBracketViewProps> = ({ tournament }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleOpenFullscreen = () => {
    navigate(`/tournaments/${tournament.id}/bracket`);
  };

  return (
    <div className="tournament-bracket-view">
      {/* Bracket Actions */}
      <div className="bracket-actions">
        <button 
          className="btn btn-primary"
          onClick={handleOpenFullscreen}
          title={t('tournaments.bracket.openFullscreen')}
        >
          ⛶ {t('tournaments.bracket.fullscreen')}
        </button>
      </div>

      {/* Bracket Tree */}
      <div className="bracket-container">
        <TournamentBracketTree tournamentId={tournament.id} />
      </div>
    </div>
  );
};

export default TournamentDetail;
